import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { StatusSelect } from "@/components/StatusSelect";
import { SendSmsDialog } from "@/components/SendSmsDialog";
import { SiteAuditDialog } from "@/components/SiteAuditDialog";
import { Download, ExternalLink, ChevronLeft, ChevronRight, Phone } from "lucide-react";

const PAGE_SIZE = 25;

export function ResultsTable({ searchId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [onlyFiltered, setOnlyFiltered] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    if (!searchId) return;
    setLoading(true);
    try {
      const res = await api.get(`/searches/${searchId}/results`, {
        params: { only_filtered: onlyFiltered, page, page_size: PAGE_SIZE },
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Erreur lors du chargement des resultats");
    } finally {
      setLoading(false);
    }
  }, [searchId, onlyFiltered, page]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    setPage(1);
  }, [onlyFiltered, searchId]);

  const updateStatus = async (placeId, statut) => {
    try {
      await api.patch(`/prospects/${placeId}/status`, { statut });
      setItems((prev) => prev.map((it) => (it.place_id === placeId ? { ...it, statut } : it)));
      toast.success("Statut mis a jour");
    } catch (e) {
      toast.error("Erreur mise a jour statut");
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/searches/${searchId}/export`, {
        params: { only_filtered: onlyFiltered },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leadfinder_export_${searchId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur export CSV");
    }
  };

  if (!searchId) return null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="border border-border" data-testid="results-table-container">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Checkbox
            id="only-filtered"
            data-testid="only-filtered-checkbox"
            checked={onlyFiltered}
            onCheckedChange={(v) => setOnlyFiltered(Boolean(v))}
            className="rounded-none"
          />
          <label htmlFor="only-filtered" className="font-mono text-xs uppercase tracking-wider cursor-pointer">
            Uniquement resultats filtres
          </label>
        </div>
        <Button data-testid="export-csv-button" onClick={handleExport} className="rounded-none font-mono text-xs uppercase tracking-wider gap-2">
          <Download size={14} /> Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Nom</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Telephone</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Adresse</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Note</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Avis</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Site Google</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Maps</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Statut</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">SMS</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Audit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.place_id} data-testid={`result-row-${r.place_id}`} className="border-border">
                <TableCell className="font-medium">{r.nom}</TableCell>
                <TableCell>
                  {r.telephone ? (
                    <span className="inline-flex items-center gap-1 font-mono text-xs">
                      <Phone size={12} /> {r.telephone}
                    </span>
                  ) : (
                    <span className="text-muted-foreground font-mono text-xs">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{r.adresse || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.note ?? "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.nb_avis ?? "-"}</TableCell>
                <TableCell>
                  {r.site_google ? (
                    <span className="font-mono text-xs text-muted-foreground">Oui</span>
                  ) : (
                    <span
                      data-testid={`no-website-badge-${r.place_id}`}
                      className="font-mono text-xs uppercase tracking-wider px-2 py-0.5 border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]"
                    >
                      Sans site Google
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {r.lien_google_maps && (
                    <a
                      href={r.lien_google_maps}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`maps-link-${r.place_id}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <ExternalLink size={12} /> Voir
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <StatusSelect value={r.statut} onChange={(v) => updateStatus(r.place_id, v)} testId={`status-select-${r.place_id}`} />
                </TableCell>
                <TableCell>
                  <SendSmsDialog placeId={r.place_id} disabled={!r.telephone} />
                </TableCell>
                <TableCell>
                  <SiteAuditDialog placeId={r.place_id} disabled={!r.site_url} />
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-10 text-muted-foreground font-mono text-xs uppercase" data-testid="results-empty-state">
                  Aucun resultat
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between p-4 border-t border-border font-mono text-xs uppercase tracking-wider">
        <span data-testid="results-total-count">{total} resultat(s)</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="rounded-none"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            data-testid="results-prev-page"
          >
            <ChevronLeft size={14} />
          </Button>
          <span data-testid="results-page-indicator">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="rounded-none"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="results-next-page"
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
